import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { generateRandomColor } from '@/lib/constants/colors';
import { useCreateClient } from '@/modules/clients/hooks/use-create-client';
import { ColorPicker } from '@/modules/common/components/molecules/color-picker';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { SingleSelectWithPagination } from '@/modules/common/components/molecules/selectors/single-select/single-select-with-pagination';
import { FileUploadField } from '@/modules/common/components/organisms/file-upload-field';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  SubmitDateRange,
  useTabFormState,
} from '@/modules/common/components/templates/feature-details';
import { IndustrySelect, ManagerSelectItem, ManagersSelect } from '@/modules/projects';
import { ResolveConflictsModal } from '@/modules/projects/components/modals/resolve-conflicts-modal';
import {
  ProjectStatusSection,
  StatusOption,
} from '@/modules/projects/components/project-details/info/project-status-section';
import { Project, ProjectStatus } from '@/modules/projects/types/project';
import { ChangeDateRange } from '@/modules/projects/types/project-conflicts';
import {
  projectInfoConflictFields,
  ProjectInfoFieldKey,
} from '@/modules/projects/utils/change-conflicts/project-info-conflict-config';
import { useProjectInfoSyncRules } from '@/modules/projects/utils/change-conflicts/project-info-sync-rules';

import { useCheckProjectInfoConflicts } from '../../../hooks/info/use-check-project-info-conflicts';
import { useProjectSelectors } from '../../../hooks/info/use-project-selectors';
import { useProjectChangeConflictResolution } from '../../../hooks/use-project-change-conflict-resolution';
import { ProjectFormData } from '../../../types';

const TAG = 'ProjectInfoTab:';

const projectInfoSchema = z.object({
  name: z.string().min(1, 'common:error.required'),
  color: z.string().nullable().default(null),
  logo: z.instanceof(File).optional(),
  status: z.nativeEnum(ProjectStatus),
  probability: z.number().min(0).max(100).optional(),
  industryIds: z.array(z.string()).default([]),
  clientId: z.string().optional(),
  pendingClientName: z.string().optional(),
  managerIds: z.array(z.string()).default([]),
  startDate: z.date().optional(),
  description: z.string().default(''),
  customAttributes: z
    .record(z.union([z.boolean(), z.string(), z.number(), z.array(z.string()), z.null()]))
    .default({}),
});

type ProjectInfoFormData = z.infer<typeof projectInfoSchema>;

export interface ProjectInfoTabProps extends BaseTabProps<Project, ProjectInfoFormData> {
  /**
   * Callback to directly save data to API after conflict resolution
   * This bypasses the normal form submission flow and conflict detection
   * @param data - The form data to save
   * @param effectiveStartDate - The effective start date for the change (from conflict resolution)
   * @param effectiveEndDate - Optional effective end date for the change
   */
  onDirectSave?: (
    data: ProjectFormData,
    effectiveStartDate?: Date,
    effectiveEndDate?: Date | null
  ) => Promise<void>;
  /**
   * Original change date range (for EditChange mode)
   * Used to determine if the date range was extended for conflict detection
   */
  originalChangeRange?: ChangeDateRange;
}

export interface ProjectInfoTabRef extends FeatureTabRef<ProjectFormData> {}

interface StatusWarningConfig {
  originalStatus: ProjectStatus | undefined;
  currentStatus: ProjectStatus;
  mode: FeatureMode;
}

/**
 * Project information form component supporting create, edit, and read modes.
 * Handles project details with draft persistence, validation, and status transitions.
 *
 * Features:
 * - Multi-mode operation with role-based field visibility
 * - Draft data persistence for create mode
 * - Status transition validation with user warnings
 * - Dynamic form fields based on project status
 * - Custom attributes support with flexible field types
 * - Real-time validation and dirty state detection
 *
 * @example
 * ```tsx
 * // Create mode with draft data
 * <ProjectInfoTab
 *   mode={FeatureMode.Create}
 *   draftData={savedDraftData}
 *   onFormStateChange={({ isDirty }) => setHasChanges(isDirty)}
 *   ref={formRef}
 * />
 *
 * // Edit mode for existing project
 * <ProjectInfoTab
 *   mode={FeatureMode.Edit}
 *   projectData={existingProject}
 *   onFormStateChange={({ isDirty }) => enableSaveButton(isDirty)}
 *   ref={formRef}
 * />
 *
 * // Read-only mode
 * <ProjectInfoTab
 *   mode={FeatureMode.Read}
 *   projectData={project}
 *   ref={formRef}
 * />
 * ```
 */
export const ProjectInfoTab = forwardRef<ProjectInfoTabRef, ProjectInfoTabProps>((props, ref) => {
  const {
    mode,
    featureData: projectData,
    draftData,
    onDirectSave,
    onFormStateChange,
    originalChangeRange,
  } = props;
  const { t } = useTranslation(['authenticated.projects', 'common']);
  // const { customAttributes } = useProjectDependencies();
  const { loadClients, searchClients } = useProjectSelectors();
  const { checkConflicts, isPending: isCheckingConflicts } = useCheckProjectInfoConflicts();
  const { mutateAsync: createClientAsync } = useCreateClient();

  // Tracks a client that has already been successfully created during this submit flow.
  // If the subsequent project save fails, we keep this id so a retry reuses the same
  // client instead of creating an orphan each time.
  const createdClientIdRef = useRef<string | null>(null);

  const [stableRandomColor] = useState(() => generateRandomColor());
  const [showStatusWarning, setShowStatusWarning] = useState(false);
  const [selectedManagerItems, setSelectedManagerItems] = useState<ManagerSelectItem[]>(
    () =>
      projectData?.info.managers?.map(m => ({
        value: m.id.toString(),
        label: m.name,
        avatarLink: m.profileImageUrl ?? null,
        isDeleted: m.isDeleted,
      })) ?? []
  );

  // Sync selected manager items when projectData loads asynchronously
  useEffect(() => {
    if (projectData?.info.managers?.length && selectedManagerItems.length === 0) {
      setSelectedManagerItems(
        projectData.info.managers.map(m => ({
          value: m.id.toString(),
          label: m.name,
          avatarLink: m.profileImageUrl ?? null,
          isDeleted: m.isDeleted,
        }))
      );
    }
  }, [projectData?.info.managers, selectedManagerItems.length]);

  /**
   * Builds initial form values based on mode and available data sources.
   * Priority: draftData (create) > projectData (edit/read) > defaults (create)
   */
  const initialValues = useMemo((): ProjectInfoFormData => {
    if (mode === FeatureMode.Create && draftData) {
      return {
        name: draftData.name || '',
        color: draftData.color || stableRandomColor,
        status: draftData.status || ProjectStatus.Active,
        probability: draftData.probability ?? undefined,
        industryIds: draftData.industryIds || [],
        clientId: draftData.clientId || '',
        pendingClientName: draftData.pendingClientName,
        managerIds: draftData.managerIds || [],
        startDate: draftData.startDate,
        description: draftData.description || '',
        customAttributes: draftData.customAttributes || {},
      };
    }

    if (mode === FeatureMode.Create) {
      return {
        name: '',
        color: stableRandomColor,
        status: ProjectStatus.Active,
        probability: undefined,
        industryIds: [],
        clientId: '',
        pendingClientName: undefined,
        managerIds: [],
        startDate: new Date(),
        description: '',
        customAttributes: {},
      };
    }

    // For edit/read mode, ensure we have stable references
    const customAttributes =
      projectData?.info.customAttributesValues?.reduce(
        (acc, attr) => {
          acc[attr.name.toLowerCase().replace(/\s+/g, '')] = attr.value;
          return acc;
        },
        {} as Record<string, any>
      ) || {};

    const industryIds = projectData?.info.industries || [];

    return {
      name: projectData?.info.name || '',
      color: projectData?.info.colorHex ?? null,
      status: projectData?.info.status || ProjectStatus.Tentative,
      probability: projectData?.info.tentativeProbability ?? undefined,
      industryIds,
      clientId: projectData?.client?.id?.toString() || '',
      pendingClientName: undefined,
      managerIds: projectData?.info.managers?.map(m => m.id.toString()) ?? [],
      startDate: projectData?.info.projectStartDate
        ? new Date(projectData.info.projectStartDate)
        : undefined,
      description: projectData?.info.description || '',
      customAttributes,
    };
  }, [mode, projectData, draftData]);

  const form = useForm<ProjectInfoFormData>({
    resolver: zodResolver(projectInfoSchema),
    mode: 'onChange',
    defaultValues: initialValues,
  });

  // Reset form when initialValues change (e.g., when projectData loads in edit mode)
  useEffect(() => {
    if (mode === FeatureMode.Edit || mode === FeatureMode.Read) {
      form.reset(initialValues);
    }
  }, [initialValues, mode, form]);

  // Non-versioned fields that don't require conflict detection
  // When only these fields change, skip conflict detection and hide date picker
  const NON_VERSIONED_FIELDS: (keyof ProjectInfoFormData)[] = [
    'name',
    'color',
    'clientId',
    'description',
  ];

  // Use the tab form state hook
  const {
    isReadMode,
    handleSubmit: baseHandleSubmit,
    getFormData: baseGetFormData,
  } = useTabFormState<Project, ProjectInfoFormData>(props, {
    form,
    initialValues,
    isArchivedFn: project => project?.info.status === ProjectStatus.Archived,
    triggerValidationOnMount: true,
    nonVersionedFields: NON_VERSIONED_FIELDS,
    ...(mode === FeatureMode.Create && {
      customDirtyStateFn: currentValues => {
        const hasRequiredName = (currentValues.name?.trim()?.length ?? 0) > 0;
        return Boolean(hasRequiredName && form.formState.isValid);
      },
    }),
  });

  const getFormData = (): ProjectFormData => baseGetFormData() as ProjectFormData;

  // Use conflict resolution hook
  const {
    showConflictModal,
    conflictSnapshots,
    selectedDateRange,
    closeConflictModal,
    handleSubmitWithConflicts,
    handleResolveConflicts,
  } = useProjectChangeConflictResolution<ProjectInfoFormData, ProjectInfoFieldKey>({
    tag: TAG,
    form,
    projectId: projectData?.id ? String(projectData.id) : undefined,
    checkConflicts,
    isCheckingConflicts,
    onFormStateChange,
    getFormData: baseGetFormData,
    onDirectSave,
    isEditChangeMode: mode === FeatureMode.EditChange,
    originalChangeRange,
  });

  const currentStatus = useWatch({ control: form.control, name: 'status' });
  const originalStatus = projectData?.info.status;

  /**
   * Determines available status options based on current project status and mode.
   * Implements business rules for status transitions to prevent invalid state changes.
   */
  const getStatusOptions = useMemo((): StatusOption[] => {
    const baseOptions: StatusOption[] = [
      { value: ProjectStatus.Active, label: t('status.active') },
      { value: ProjectStatus.Tentative, label: t('status.tentative') },
      { value: ProjectStatus.Archived, label: t('status.archived') },
    ];

    if (mode === FeatureMode.Create) {
      return baseOptions.filter(option => option.value !== ProjectStatus.Archived);
    }

    switch (originalStatus) {
      case ProjectStatus.Active:
        return [baseOptions[0], { ...baseOptions[1], isItemDisabled: true }];

      case ProjectStatus.Tentative:
        return [baseOptions[0], baseOptions[1]];

      case ProjectStatus.Archived:
        return [
          { ...baseOptions[0], isItemDisabled: true },
          { ...baseOptions[1], isItemDisabled: true },
          baseOptions[2],
        ];

      default:
        return baseOptions;
    }
  }, [mode, originalStatus, t]);

  /**
   * Determines if status change warning should be displayed based on business rules.
   */
  const shouldShowStatusWarning = useMemo((): boolean => {
    const config: StatusWarningConfig = { originalStatus, currentStatus, mode };
    return (
      config.originalStatus === ProjectStatus.Tentative &&
      config.currentStatus === ProjectStatus.Active &&
      config.mode !== FeatureMode.Create
    );
  }, [originalStatus, currentStatus, mode]);

  useEffect(() => {
    setShowStatusWarning(shouldShowStatusWarning);
  }, [shouldShowStatusWarning]);

  useEffect(() => {
    if (currentStatus !== ProjectStatus.Tentative) {
      form.setValue('probability', undefined);
      void form.trigger('probability');
    }
  }, [currentStatus, form]);

  const shouldShowWarningForDeletedEntity = (item: DropdownSearchItem) => {
    return item.isDeleted ? t('details.deletedEntityWanring') : undefined;
  };

  // Initial options for manager multi-select (includes deleted managers so they still display)
  const initialManagerOptions = useMemo(
    (): ManagerSelectItem[] =>
      projectData?.info.managers?.map(m => ({
        value: m.id.toString(),
        label: m.name,
        avatarLink: m.profileImageUrl ?? null,
        isDeleted: m.isDeleted,
      })) ?? [],
    [projectData?.info.managers]
  );

  // Warn when any selected manager is archived — mirrors the department/role/seniority pattern
  // in the team member form where a warning is shown whenever a selected entity is deleted.
  const anyManagerDeleted = useMemo(
    () => selectedManagerItems.some(m => m.isDeleted),
    [selectedManagerItems]
  );

  // Initial selected item for client selector (displays immediately without pre-fetching)
  // Also used as additionalItems if client is deleted (won't be returned by API)
  const initialClientItem = useMemo((): DropdownSearchItem | undefined => {
    if (!projectData?.client) return undefined;
    return {
      id: projectData.client.id.toString(),
      name: projectData.client.name,
      avatarLink: projectData.client.logoUrl ?? undefined,
      isDeleted: projectData.client.isDeleted,
    };
  }, [projectData?.client]);

  // Only include as additionalItems if client is deleted (otherwise API will return it)
  const additionalClientItems = useMemo(() => {
    if (initialClientItem?.isDeleted) {
      return [initialClientItem];
    }
    return undefined;
  }, [initialClientItem]);

  // Sentinel id used as the form value while a pending (yet-to-be-created) client name is selected.
  // Real client ids from the backend are numeric strings, so this will never collide.
  const PENDING_CLIENT_ID = '__pending__';
  const pendingClientName = useWatch({ control: form.control, name: 'pendingClientName' });
  const clientIdValue = useWatch({ control: form.control, name: 'clientId' });

  // When a pending client name is set, expose it to SingleSelectWithPagination as the
  // currently selected item so the input shows the user's chosen name.
  const pendingClientSelectedItem = useMemo((): DropdownSearchItem | undefined => {
    if (!pendingClientName || clientIdValue !== PENDING_CLIENT_ID) return undefined;
    return {
      id: PENDING_CLIENT_ID,
      name: pendingClientName,
    };
  }, [pendingClientName, clientIdValue]);

  const effectiveInitialClientItem = pendingClientSelectedItem || initialClientItem;
  const effectiveAdditionalClientItems = useMemo(() => {
    if (pendingClientSelectedItem) return [pendingClientSelectedItem];
    return additionalClientItems;
  }, [pendingClientSelectedItem, additionalClientItems]);

  // onCreateOption contract (from SingleSelect): return a SingleSelectOption whose `value`
  // becomes the form field value. We return PENDING_CLIENT_ID as the value so the form tracks
  // that a pending creation is in flight, and separately stash the chosen name in
  // `pendingClientName` for the alert + submit-time client creation.
  const handleCreateClientOption = (search: string) => {
    const trimmed = search.trim();
    // Any previously pre-created client id is invalidated — the name may differ now.
    createdClientIdRef.current = null;
    form.setValue('pendingClientName', trimmed, { shouldDirty: true, shouldValidate: true });
    return { value: PENDING_CLIENT_ID, label: trimmed };
  };

  // Clear pendingClientName when the user clears the client or picks a real client.
  useEffect(() => {
    if (pendingClientName && clientIdValue !== PENDING_CLIENT_ID) {
      form.setValue('pendingClientName', undefined, { shouldDirty: true });
      createdClientIdRef.current = null;
    }
  }, [clientIdValue, pendingClientName, form]);
  const conflictModalSyncRules = useProjectInfoSyncRules(
    form.getValues('probability') ?? null,
    conflictSnapshots
  );

  /**
   * Builds form values enriched with display names for the conflict resolution modal.
   * managerIds is passed as [{ id, name }, ...] so the modal can display names directly,
   * matching the shape of snapshot data (which also has managers as [{ id, name }, ...]).
   */
  const getConflictModalFormValues = (): Record<ProjectInfoFieldKey, any> => {
    const values = form.getValues();
    return {
      ...values,
      managerIds:
        selectedManagerItems.length > 0
          ? selectedManagerItems.map(m => ({ id: m.value, name: m.label }))
          : [],
    } as Record<ProjectInfoFieldKey, any>;
  };

  // Snapshot form values keyed to the current set of conflict snapshots.
  // conflictSnapshots only changes when new conflicts are detected (i.e. when
  // the modal is about to open), so this memo is stable while the modal is open
  // and prevents useConflictResolution from resetting its internal form on
  // every parent re-render.
  const conflictFormValues = useMemo(
    () => getConflictModalFormValues(),
    [conflictSnapshots] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /**
   * If the user chose to create a client inline, create it (or reuse a previously-created id
   * on retry) and patch the form so the downstream submit path sends a real clientId.
   * Throws on client-create failure — the project save is skipped and the user can retry.
   */
  const ensurePendingClientCreated = async (): Promise<void> => {
    const name = form.getValues('pendingClientName')?.trim();
    const currentClientId = form.getValues('clientId');

    if (!name || currentClientId !== PENDING_CLIENT_ID) return;

    if (createdClientIdRef.current) {
      // Previous attempt already created the client — reuse the id and let submit proceed.
      form.setValue('clientId', createdClientIdRef.current, { shouldDirty: false });
      form.setValue('pendingClientName', undefined, { shouldDirty: false });
      return;
    }

    const created = await createClientAsync({ name });
    const newId = String(created.id);
    createdClientIdRef.current = newId;
    form.setValue('clientId', newId, { shouldDirty: false });
    form.setValue('pendingClientName', undefined, { shouldDirty: false });
  };

  /**
   * Submit handler with conflict detection
   */
  const handleSubmit = async (dateRange?: SubmitDateRange): Promise<void> => {
    await ensurePendingClientCreated();

    // Skip conflict detection for Create mode
    if (mode === FeatureMode.Create) {
      await baseHandleSubmit();
      return;
    }

    // Use the conflict resolution hook for Edit/EditChange modes
    await handleSubmitWithConflicts(baseHandleSubmit, dateRange);
  };

  const tabContent = (
    <Form {...form}>
      <form className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('details.tabs.info.mainInfoTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="shrink-0">
                <ColorPicker
                  name="color"
                  control={form.control}
                  label={t('details.tabs.info.color')}
                  disabled={isReadMode}
                />
              </div>
              <div className="flex-1">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('details.tabs.info.name')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('details.tabs.info.namePlaceholder')}
                          disabled={isReadMode}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <ProjectStatusSection
              form={form}
              isReadMode={isReadMode}
              statusOptions={getStatusOptions}
              showWarning={showStatusWarning}
              currentStatus={currentStatus}
              t={t}
            />

            {mode === FeatureMode.Create && (
              <FileUploadField
                name="logo"
                control={form.control}
                label={t('details.tabs.info.logo')}
                disabled={isReadMode}
              />
            )}

            <IndustrySelect
              name="industryIds"
              control={form.control}
              label={t('details.tabs.info.industries')}
              placeholder={
                isReadMode
                  ? t('details.tabs.info.notSelected')
                  : t('details.tabs.info.industriesPlaceholder')
              }
              disabled={isReadMode}
            />

            <SingleSelectWithPagination
              name="clientId"
              control={form.control}
              showWarning={shouldShowWarningForDeletedEntity}
              initialSelectedItem={effectiveInitialClientItem}
              label={t('details.tabs.info.client')}
              placeholder={
                isReadMode
                  ? t('details.tabs.info.notSelected')
                  : t('details.tabs.info.clientPlaceholder')
              }
              additionalItems={effectiveAdditionalClientItems}
              disabled={isReadMode}
              showAvatars={true}
              showDescriptionInOptions={false}
              showDescriptionInSelector={false}
              avatarShape="rounded"
              queryKey={['clients-short']}
              loadItems={loadClients}
              searchItems={searchClients}
              isClearSelectionEnabled={true}
              isCreatable={!isReadMode}
              onCreateOption={handleCreateClientOption}
            />
            {pendingClientName && clientIdValue === PENDING_CLIENT_ID && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  {t('details.tabs.info.pendingClientAlert', { name: pendingClientName })}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('details.tabs.info.additionalInfoTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ManagersSelect
              name="managerIds"
              control={form.control}
              label={t('details.tabs.info.manager')}
              placeholder={
                isReadMode
                  ? t('details.tabs.info.notSelected')
                  : t('details.tabs.info.managerPlaceholder')
              }
              disabled={isReadMode}
              initialOptions={initialManagerOptions}
              onSelectItems={setSelectedManagerItems}
            />
            {anyManagerDeleted && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>{t('details.deletedManagerWarning')}</AlertDescription>
              </Alert>
            )}

            <DatePicker
              name="startDate"
              control={form.control}
              label={t('details.tabs.info.startDate')}
              inputDisabled={
                isReadMode ||
                ((mode === FeatureMode.Edit || mode === FeatureMode.EditChange) &&
                  !!projectData?.info.projectStartDate)
              }
              placeholder={isReadMode ? t('details.tabs.info.notSelected') : undefined}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('details.tabs.info.description')}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t('details.tabs.info.descriptionPlaceholder')}
                      rows={4}
                      disabled={isReadMode}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/*TODO Custom attributes decided not to add in MVP. Uncomment this section later*/}
        {/*<Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="flex-1">{t('details.tabs.info.customAttributesTitle')}</CardTitle>
            {!isReadMode && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 flex-shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium leading-5"
                onClick={() => {
                  // TODO: Implement navigation to settings page
                }}
              >
                {t('details.tabs.info.manageAttributes')}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <CustomAttributesFields
              customAttributes={customAttributes}
              form={form}
              isReadMode={isReadMode}
              t={t}
            />
          </CardContent>
        </Card>*/}
      </form>
    </Form>
  );

  return (
    <>
      <BaseFeatureTab
        {...props}
        ref={ref}
        isArchivedFn={project => project?.info.status === ProjectStatus.Archived}
        onSubmit={handleSubmit}
        isValid={form.formState.isValid}
        getFormData={getFormData}
        isCheckingConflicts={isCheckingConflicts}
      >
        {tabContent}
      </BaseFeatureTab>

      {/* Conflict Resolution Modal */}
      {showConflictModal && selectedDateRange && (
        <ResolveConflictsModal
          isOpen={showConflictModal}
          onClose={closeConflictModal}
          snapshots={conflictSnapshots}
          currentFormValues={conflictFormValues}
          selectedDateRange={selectedDateRange}
          fields={projectInfoConflictFields}
          onResolve={handleResolveConflicts}
          t={t}
          translationNamespace="authenticated.projects"
          fieldSyncRules={conflictModalSyncRules}
        />
      )}
    </>
  );
});
